/**
 * Hook de debounce pour optimiser les performances
 *
 * Retarde l'exécution d'une fonction jusqu'à ce qu'un certain
 * délai se soit écoulé depuis le dernier appel.
 *
 * @module useDebounce
 * @author Équipe Brydge
 * @since 2.3.0
 */

import {useState, useEffect} from 'react';

/**
 * Hook pour debouncer une valeur
 *
 * Permet d'éviter les appels trop fréquents d'une fonction (ex: recherche API)
 * en attendant que l'utilisateur ait fini de taper.
 *
 * @param {any} value - Valeur à debouncer
 * @param {number} delay - Délai en millisecondes
 * @return {any} Valeur debouncée
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm, 500);
 *
 * useEffect(() => {
 *   if (debouncedSearch) {
 *     fetchSearchResults(debouncedSearch);
 *   }
 * }, [debouncedSearch]);
 */
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
